<template>
  <div class="salon-app salon-cal-root" role="dialog" aria-modal="true" :aria-label="$t('salon.calendar.ariaLabel')">
    <div class="salon-cal-scrim" @click="$emit('close')"></div>

    <div class="salon-cal-sheet">
      <span class="salon-cal-handle" aria-hidden="true"></span>

      <div class="salon-cal-head">
        <h3 class="salon-cal-title">{{ monthText }}</h3>
        <button class="salon-cal-close" type="button" :aria-label="$t('salon.calendar.close')" @click="$emit('close')">
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" />
          </svg>
        </button>
      </div>

      <div class="salon-cal-body">
        <div class="salon-cal-weekdays" aria-hidden="true">
          <span v-for="(label, index) in weekdayLabels" :key="index">{{ label }}</span>
        </div>

        <div class="salon-cal-grid" role="grid">
          <!-- The month never starts on a Sunday by luck, so the first row is padded
               out; without it every day would sit under the wrong weekday. -->
          <span v-for="blank in leadingBlanks" :key="'blank-' + blank" class="salon-cal-blank"></span>

          <button
            v-for="day in days"
            :key="day.date"
            class="salon-cal-day"
            :class="{
              'salon-cal-day--selected': day.date === selectedDate,
              'salon-cal-day--off': !day.is_selectable,
              'salon-cal-day--today': day.date === today,
            }"
            type="button"
            role="gridcell"
            :disabled="!day.is_selectable"
            :aria-label="day.hours_text"
            :aria-pressed="String(day.date === selectedDate)"
            @click="$emit('select', day)"
          >
            <span class="salon-cal-day-number">{{ day.day_number }}</span>
            <!-- SH026 — a stylist's day off is neither full nor closed. -->
            <span v-if="day.is_staff_off" class="salon-cal-day-tag">{{ $t("salon.calendar.off") }}</span>
            <!-- SH037 — the service itself is not on the menu that day. -->
            <span v-else-if="day.is_service_off" class="salon-cal-day-tag">{{ $t("salon.calendar.notOffered") }}</span>
            <span v-else-if="day.is_open && !day.is_past && day.is_full" class="salon-cal-day-tag">{{ $t("salon.calendar.full") }}</span>
            <span v-else-if="!day.is_open && !day.is_past" class="salon-cal-day-tag">{{ $t("salon.calendar.closed") }}</span>
          </button>
        </div>

        <div class="salon-cal-legend">
          <span class="salon-cal-legend-item">
            <span class="salon-cal-swatch salon-cal-swatch--selected"></span>{{ $t("salon.calendar.selected") }}
          </span>
          <span class="salon-cal-legend-item">
            <span class="salon-cal-swatch"></span>{{ $t("salon.calendar.available") }}
          </span>
          <span class="salon-cal-legend-item">
            <span class="salon-cal-swatch salon-cal-swatch--off"></span>{{ $t("salon.calendar.unavailable") }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import "@/salon/styles/tokens.css";

export default {
  name: "SalonMonthCalendarSheet",
  props: {
    // Every day of the month, straight from member/salon/booking/available-dates.
    days: {
      type: Array,
      default: () => [],
    },
    monthText: {
      type: String,
      default: "",
    },
    selectedDate: {
      type: String,
      default: "",
    },
    today: {
      type: String,
      default: "",
    },
  },
  emits: ["close", "select"],
  computed: {
    // SH046 — Chinese reads the column heads off Intl (日 一 二 …); 1–7 Sep 2025 run
    // Sunday to Saturday, and UTC on both sides keeps the day from shifting.
    weekdayLabels() {
      if (this.$i18n.locale !== "zh") {
        return ["S", "M", "T", "W", "T", "F", "S"];
      }

      const format = new Intl.DateTimeFormat("zh-CN", { timeZone: "UTC", weekday: "narrow" });

      return [0, 1, 2, 3, 4, 5, 6].map((index) => format.format(new Date(Date.UTC(2025, 8, 7 + index))));
    },

    leadingBlanks() {
      const first = this.days[0];

      if (!first) {
        return 0;
      }

      // Read the weekday off the API's own day_short rather than parsing the date in
      // the browser: `new Date("2026-09-01")` is parsed as UTC, which lands on the
      // previous day for anyone west of Greenwich and shifts the whole grid.
      const index = this.weekdayOrder.indexOf(first.day_short);

      return index === -1 ? 0 : index;
    },

    weekdayOrder() {
      return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    },
  },
};
</script>

<style scoped>
/* Tokens come from src/salon/styles/tokens.css, namespaced under .salon-app. */
.salon-cal-root {
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  font-family: var(--salon-font-body);
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-on-surface);
}

.salon-cal-scrim {
  position: absolute;
  inset: 0;
  background: rgba(1, 1, 0, 0.4);
}

.salon-cal-sheet {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: var(--salon-max-width);
  margin: 0 auto;
  overflow: hidden;
  background: var(--salon-surface);
  border-radius: 24px 24px 0 0;
  box-shadow: 0 -4px 20px rgba(28, 28, 30, 0.08);
}

.salon-cal-handle {
  position: absolute;
  top: 12px;
  left: 50%;
  width: 48px;
  height: 6px;
  background: var(--salon-surface-variant);
  border-radius: 999px;
  opacity: 0.6;
  transform: translateX(-50%);
}

.salon-cal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 32px var(--salon-container-padding) 8px;
}

.salon-cal-title {
  margin: 0;
  font-family: var(--salon-font-headline);
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  color: var(--salon-primary);
}

.salon-cal-close {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  color: var(--salon-on-surface-variant);
  background: transparent;
  border: 0;
  cursor: pointer;
}

.salon-cal-body {
  padding: 8px var(--salon-container-padding)
    calc(24px + env(safe-area-inset-bottom, 0px));
}

.salon-cal-weekdays,
.salon-cal-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
}

.salon-cal-weekdays {
  margin-bottom: 8px;
}

.salon-cal-weekdays span {
  font-size: 12px;
  line-height: 16px;
  color: var(--salon-on-surface-variant);
  text-align: center;
}

.salon-cal-blank {
  height: 44px;
}

.salon-cal-day {
  display: flex;
  flex-direction: column;
  gap: 1px;
  align-items: center;
  justify-content: center;
  height: 44px;
  padding: 0;
  font-family: var(--salon-font-body);
  color: var(--salon-on-background);
  background: var(--salon-surface-lowest);
  border: 1px solid var(--salon-border);
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.salon-cal-day:hover:not(:disabled) {
  border-color: var(--salon-primary);
}

.salon-cal-day--today:not(.salon-cal-day--selected) {
  border-color: var(--salon-on-surface-variant);
}

.salon-cal-day--selected {
  color: var(--salon-on-primary);
  background: var(--salon-primary);
  border-color: var(--salon-primary);
}

.salon-cal-day--off {
  color: var(--salon-on-surface-variant);
  background: var(--salon-surface-container);
  border-color: transparent;
  cursor: not-allowed;
  opacity: 0.5;
}

.salon-cal-day-number {
  font-size: 15px;
  font-weight: 500;
  line-height: 18px;
}

.salon-cal-day--off .salon-cal-day-number {
  text-decoration: line-through;
}

.salon-cal-day-tag {
  font-size: 9px;
  line-height: 10px;
  color: var(--salon-error);
}

.salon-cal-legend {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: center;
  padding-top: 16px;
  margin-top: 16px;
  border-top: 1px solid var(--salon-border);
}

.salon-cal-legend-item {
  display: flex;
  gap: 6px;
  align-items: center;
  font-size: 12px;
  line-height: 16px;
  color: var(--salon-on-surface-variant);
}

.salon-cal-swatch {
  width: 12px;
  height: 12px;
  background: var(--salon-surface-lowest);
  border: 1px solid var(--salon-border);
  border-radius: 4px;
}

.salon-cal-swatch--selected {
  background: var(--salon-primary);
  border-color: var(--salon-primary);
}

.salon-cal-swatch--off {
  background: var(--salon-surface-container);
  border-color: transparent;
  opacity: 0.6;
}
</style>
